import React from 'react';
import styled, { useTheme } from 'styled-components';
import type { CompletenessStatus } from '../../utils/profileSectionCompleteness';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';

export interface SectionCompletenessBadgeProps {
  percentage: number;
  status: CompletenessStatus;
  statusLabel: string;
  /** Compact ring for section headers; larger for overall summary. */
  size?: 'sm' | 'md';
  /** Accessible name, e.g. "Personal information completeness" */
  ariaLabel?: string;
}

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-shrink: 0;
`;

const RingWrap = styled.div<{ $size: number }>`
  position: relative;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  flex-shrink: 0;
`;

const RingSvg = styled.svg`
  display: block;
  transform: rotate(-90deg);
`;

const PctLabel = styled.span<{ $color: string; $size: 'sm' | 'md' }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${props =>
    props.$size === 'sm' ? typography.fontSize.xs : typography.fontSize.sm};
  color: ${props => props.$color};
  line-height: 1;
`;

const StatusPill = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px ${spacing[2]};
  border-radius: ${borders.radius.sm};
  background: ${props => props.$bg};
  color: ${props => props.$color};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  letter-spacing: 0.01em;
  white-space: nowrap;
  transition: background ${animations.duration.fast} ${animations.easing.easeOut};
`;

function useStatusColors(status: CompletenessStatus) {
  const theme = useTheme();
  switch (status) {
    case 'complete':
      return {
        ring: theme.colors.success,
        track: theme.colors.successBg || `${theme.colors.success}22`,
        pillBg: theme.colors.successBg || `${theme.colors.success}18`,
        pillFg: theme.colors.success,
        text: theme.colors.success,
      };
    case 'partial':
      return {
        ring: theme.colors.icomGold || theme.colors.accent || theme.colors.warning,
        track: theme.colors.warningBg || `${theme.colors.warning}22`,
        pillBg: theme.colors.warningBg || `${theme.colors.warning}18`,
        pillFg: theme.colors.warning,
        text: theme.colors.warning,
      };
    case 'notApplicable':
      return {
        ring: theme.colors.textMuted || theme.colors.textSecondary,
        track: theme.colors.icomWarmGray || theme.colors.surfaceHover,
        pillBg: theme.colors.icomWarmGray || theme.colors.surfaceHover,
        pillFg: theme.colors.textSecondary,
        text: theme.colors.textSecondary,
      };
    case 'incomplete':
    default:
      return {
        ring: theme.colors.error,
        track: theme.colors.errorBg || `${theme.colors.error}22`,
        pillBg: theme.colors.errorBg || `${theme.colors.error}18`,
        pillFg: theme.colors.error,
        text: theme.colors.error,
      };
  }
}

export const SectionCompletenessBadge: React.FC<SectionCompletenessBadgeProps> = ({
  percentage,
  status,
  statusLabel,
  size = 'sm',
  ariaLabel,
}) => {
  const colors = useStatusColors(status);
  const dim = size === 'sm' ? 40 : 56;
  const stroke = size === 'sm' ? 3.5 : 4.5;
  const radius = (dim - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percentage));
  const offset =
    status === 'notApplicable'
      ? 0
      : circumference - (clamped / 100) * circumference;

  const displayPct = status === 'notApplicable' ? '—' : `${clamped}%`;

  return (
    <Wrapper
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={status === 'notApplicable' ? undefined : clamped}
      aria-valuetext={
        status === 'notApplicable' ? statusLabel : `${clamped}% — ${statusLabel}`
      }
      aria-label={ariaLabel || statusLabel}
    >
      <RingWrap $size={dim}>
        <RingSvg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} aria-hidden>
          <circle
            cx={dim / 2}
            cy={dim / 2}
            r={radius}
            fill="none"
            stroke={colors.track}
            strokeWidth={stroke}
          />
          <circle
            cx={dim / 2}
            cy={dim / 2}
            r={radius}
            fill="none"
            stroke={colors.ring}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{
              transition: `stroke-dashoffset ${animations.duration.normal}ms ${animations.easing.easeOut}`,
            }}
          />
        </RingSvg>
        <PctLabel $color={colors.text} $size={size}>
          {displayPct}
        </PctLabel>
      </RingWrap>
      <StatusPill $bg={colors.pillBg} $color={colors.pillFg}>
        {statusLabel}
      </StatusPill>
    </Wrapper>
  );
};

export default SectionCompletenessBadge;
